// /guide - «Setup guide»: install and connect every source on one public, pre-rendered page (ADR-0036).
// The texts are the ones of the instruction modals (GuideBody, guide.* keys); anchors here are sections,
// so GuideHost is not mounted. Nothing touches window/document while rendering - effects only.
import { Title } from "./Hl";
import { useEffect } from "react";
import { Link, useLocation } from "react-router";
import { t } from "../i18n";
import { LANDING_SOURCES, guideAnchor, guideText, lines, splitError, type GuideId } from "../guides";
import { landingFor } from "../landing";
import { ClaudeLinks } from "./ClaudeLinks";
import { ErrorList, GuideBody, Rich } from "./Guide";
import { CONNECTOR_URL, MARKETPLACE_ADD_COMMAND, MigrateSteps, PLUGIN_INSTALL_COMMAND, START_MESSAGE } from "./InstallPage";
import { ChatgptPluginLink } from "./ChatGpt";
import { SOURCE_ICON, SourceIcon } from "./brand-icons";
import { PRICING_PUBLIC } from "../flags";
import { CLIENTS_DEFAULT_MAX, countWord } from "../lib";

/** Scrolls the section named by the URL hash into view (client navigation does not do it by itself). */
function useScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView?.();
  }, [hash]);
}

function Intro({ id }: { id: GuideId }) {
  return (
    <p>
      <Rich text={guideText(id).intro} />
    </p>
  );
}

export function GuidePage() {
  useScrollToHash();
  return (
    <article className="landing guide-page">
      <header className="landing-hero">
        <h1><Title text={t("guidePage.h1")} /></h1>
        <p className="hero-lead">{t("guidePage.lead")}</p>
      </header>

      <nav className="landing-section" aria-label={t("guidePage.toc")}>
        <h2>{t("guidePage.toc")}</h2>
        <ul>
          <li>
            <Link to={{ hash: "#install" }}>{t("guidePage.install")}</Link>
            <ul>
              <li>
                <Link to={{ hash: "#plugin" }}>{guideText("plugin").title}</Link>
              </li>
              <li>
                <Link to={{ hash: "#chatgpt" }}>{guideText("chatgpt").title}</Link>
              </li>
              <li>
                <Link to={{ hash: "#mcp" }}>{t("install.mcpTitle")}</Link>
              </li>
              <li>
                <Link to={{ hash: "#claude-code" }}>{t("install.codeTitle")}</Link>
              </li>
              <li>
                <Link to={{ hash: "#migrate" }}>{t("install.migrateSummary")}</Link>
              </li>
            </ul>
          </li>
          <li>
            {t("guidePage.sources")}
            <ul>
              {LANDING_SOURCES.map(({ id }) => (
                <li key={id}>
                  <Link to={{ hash: `#${guideAnchor(id)}` }}>{guideText(id).title}</Link>
                </li>
              ))}
            </ul>
          </li>
          <li>
            <Link to={{ hash: "#clients" }}>{t("guidePage.clients")}</Link>
          </li>
          <li>
            <Link to={{ hash: "#common" }}>{t("guide.commonTitle")}</Link>
          </li>
        </ul>
      </nav>

      <section className="landing-section" id="install">
        <h2>{t("guidePage.install")}</h2>
        {PRICING_PUBLIC && (
          <p data-testid="guide-pricing">
            {t("pricing.guideLine")}{" "}
            <Link className="text-link" to="/pricing">
              {t("nav.pricing")} →
            </Link>
          </p>
        )}

        <section className="landing-section" id="plugin">
          <h3>{guideText("plugin").title}</h3>
          <Intro id="plugin" />
          <GuideBody id="plugin" level={4} common={false} afterSteps={<ClaudeLinks />} />
          <p>
            {t("install.phoneNote")}
            <Link to={{ hash: "#claude-code" }}>{t("install.phoneNoteLink")}</Link>.
          </p>
        </section>

        <section className="landing-section" id="chatgpt">
          <h3>{guideText("chatgpt").title}</h3>
          <Intro id="chatgpt" />
          <GuideBody
            id="chatgpt"
            level={4}
            common={false}
            afterSteps={
              <div className="actions">
                <ChatgptPluginLink />
              </div>
            }
          />
        </section>

        {/* «Other apps (MCP)» on /install (owner 2026-11-02): the same texts. */}
        <section className="landing-section" id="mcp">
          <h3>{t("install.mcpTitle")}</h3>
          <p>{t("install.mcpLead")}</p>
          <ol>
            <li>
              {t("install.mcp1")} <code>{CONNECTOR_URL}</code>. {t("install.mcp1Sub")}
            </li>
            <li>
              {t("install.mcp2a")}
              {t("install.mcp2Em")}
              {t("install.mcp2b")}. {t("install.plugin4Sub")}.
            </li>
            <li>
              {t("install.then1")}
              <Link to="/connections">{t("install.thenLink")}</Link>
              {t("install.mcp3")} <code>{START_MESSAGE}</code>
            </li>
          </ol>
          <h4>{t("install.authTitle")}</h4>
          <ol>
            {(["install.auth1", "install.auth2", "install.auth3", "install.auth4"] as const).map((k) => (
              <li key={k}>{t(k)}</li>
            ))}
            <li>
              {t("install.auth5")}
              <Link to="/connections#assistants">{t("install.auth5Link")}</Link>.
            </li>
          </ol>
          <p>{t("install.authNoOauth")}</p>
          <section id="claude-code">
            <h4>{t("install.codeTitle")}</h4>
            <p>{t("install.codeLead")}</p>
            <p>
              <code>{MARKETPLACE_ADD_COMMAND}</code>
              <br />
              <code>{PLUGIN_INSTALL_COMMAND}</code>
            </p>
            <p>{t("install.code")}</p>
          </section>
          <h4>{t("install.connectorTitle")}</h4>
          <p>{t("install.connector")}</p>
          <p>
            <code>{CONNECTOR_URL}</code>
          </p>
          <section id="migrate">
            <h4>{t("install.migrateSummary")}</h4>
            <MigrateSteps heading={5} />
          </section>
        </section>
      </section>

      {LANDING_SOURCES.map(({ id }) => {
        const g = guideText(id);
        const more = landingFor(id);
        return (
          <section className="landing-section" id={guideAnchor(id)} key={id} data-guide-section={id}>
            <h2 className="guide-source-title">
              {SOURCE_ICON[id] && (
                <span className="src-icon" aria-hidden="true">
                  <SourceIcon id={id} size={20} />
                </span>
              )}
              {g.title}
            </h2>
            <Intro id={id} />
            <GuideBody id={id} level={3} common={false} />
            <p>
              <Link className="btn btn-secondary" to={`/connections#${guideAnchor(id)}`}>
                {t("guidePage.connect")}
              </Link>
              {more && (
                <>
                  {" "}
                  <Link className="text-link" to={more.path} data-testid="guide-more">
                    {t("guide.more", { source: g.title })}
                  </Link>
                </>
              )}
            </p>
          </section>
        );
      })}

      <section className="landing-section" id="clients" data-testid="guide-clients">
        <h2>{t("guide.clients.title")}</h2>
        <p>{t("guide.clients.what")}</p>
        <p>{t("guide.clients.add")}</p>
        <p id="clients-elsewhere" data-testid="guide-clients-elsewhere">
          <strong>{t("guide.clients.elsewhereTitle")}.</strong> {t("guide.clients.elsewhere")}
        </p>
        <p>{t("guide.clients.ask")}</p>
        <p>
          {t("guide.clients.limit", { count: countWord(CLIENTS_DEFAULT_MAX) })}
          <Link className="text-link" to="/clients/more">
            {t("guide.clients.moreLink")}
          </Link>
          .
        </p>
        <p>{t("guide.clients.partner")}</p>
        <p>
          <Link className="text-link" to="/for-agencies" data-testid="guide-agencies">
            {t("guide.clients.agencies")}&nbsp;→
          </Link>
        </p>
      </section>

      <section className="landing-section" id="common">
        <h2>{t("guide.commonTitle")}</h2>
        <ErrorList items={lines(t("guide.common.errors")).map(splitError)} />
      </section>
    </article>
  );
}
